/**
 * Client-side wrapper for the DXF parser Web Worker.
 *
 * Keeps the UI thread responsive during the ~2-3s parse of large DXF
 * files (11 MB АБИ1.dxf) by running tokenize → entities → bridge in a
 * worker. The worker emits progress events which we forward to the
 * caller's `onProgress` callback, and resolves with the final
 * `DrawingDocument` POJO.
 *
 * Feature detection is performed up front via {@link isWorkerSupported}.
 * When unavailable (SSR, old browsers, jsdom test environments) the
 * caller is expected to fall back to the synchronous import path.
 *
 * Cancellation:
 *   - `startDxfImportInWorker` returns an `abort()` function that posts
 *     a cancel message to the worker and then terminates it after the
 *     worker reports `aborted`.
 *   - If `abort()` is called before the worker has even started
 *     parsing (i.e. while message is still in the queue), the worker
 *     still sees the cancel and responds with `aborted` before the
 *     parse begins.
 */
import type {
  DrawingDocument,
  DrawingEntity,
  ImportPhaseName,
  ReferenceFix,
} from "@dxf-ai-editor/drawing-model";

// post-V9 Section 0 — instrument the main-thread receive boundary so the
// runner can attribute structured-clone deserialization cost separately
// from the worker-side parse work.
import {
  markStart as section0MarkStart,
  measureSince as section0MeasureSince,
} from "./section0Instrumentation";
import {
  recordWorkerProgressMsg,
  recordWorkerChunkMsg,
} from "./importMetrics";

/**
 * Result handle returned by {@link startDxfImportInWorker}. Callers
 * `await` `promise` to get the parsed document and may call `abort()`
 * at any point before resolution to cancel.
 */
export interface WorkerImportResult {
  /**
   * Resolves with the parsed `DrawingDocument` when the worker reports
   * `result`. Rejects with `AbortError` on cancellation or `Error` on
   * parser failure.
   *
   * The value is typed as `DrawingDocument` but comes from the worker
   * via structured clone — callers SHOULD revalidate with
   * `drawingDocumentSchema.parse()` if they need hard schema guarantees
   * (the store does this implicitly through downstream pipeline steps).
   */
  promise: Promise<DrawingDocument>;
  /** Request cancellation. Idempotent — safe to call multiple times. */
  abort: () => void;
}

/**
 * Progress callback signature forwarded from the worker to the caller.
 * Mirrors {@link ImportProgressCallback} from `drawing-model`.
 */
export type WorkerImportProgressCallback = (
  phase: ImportPhaseName,
  percent: number,
  itemsProcessed: number,
  itemsTotal: number,
) => void;

/**
 * Feature-detect Web Worker availability. Returns `false` in jsdom
 * (tests), SSR and environments that don't expose `crypto.randomUUID`
 * (needed for correlation IDs). Callers should fall back to the
 * synchronous import path in that case.
 */
export function isWorkerSupported(): boolean {
  return (
    typeof Worker !== "undefined" &&
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function" &&
    typeof URL !== "undefined"
  );
}

type ParseInMsg = { id: string; cmd: "parse"; source: string };
type ParseFileInMsg = { id: string; cmd: "parseFile"; file: File; stream?: boolean };
type AbortInMsg = { id: string; cmd: "abort" };

type ProgressOutMsg = {
  id: string;
  type: "progress";
  phase: ImportPhaseName;
  percent: number;
  itemsProcessed: number;
  itemsTotal: number;
};
type ResultOutMsg = {
  id: string;
  type: "result";
  document: DrawingDocument;
  // Section 0 — span list captured inside the worker realm. Forwarded to
  // a `globalThis.__section0_workerMeasures` bucket on receipt so the
  // out-of-process runner can merge them into the main-thread timeline.
  section0Measures?: Array<{ name: string; startTime: number; duration: number }>;
};
/**
 * Phase 4 Wave 2 Task 4 — entity-streaming chunk.
 *
 * The worker's `parseFile` (huge-file) path emits these in lieu of
 * populating `space.entities[]` in the final result. The client's
 * `onEntityChunk` callback (typically the editorStore importDxfFileAsync
 * handler) appends each entity to the compact cache. By the time the
 * `result` message arrives, the cache is already authoritative — the
 * caller MUST skip the usual `populateCompactCacheFromDocument(doc)`
 * step on the streaming path because the result document carries empty
 * per-space entity arrays.
 *
 * Only emitted by `startDxfImportFromFileInWorker`. The text path
 * (`startDxfImportInWorker`) does NOT stream — it keeps the legacy
 * single-shot `result` message shape for backward compatibility.
 */
type EntityChunkOutMsg = {
  id: string;
  type: "entity-chunk";
  spaceId: string;
  entities: DrawingEntity[];
};
/**
 * PR1b Phase 2b — streaming per-chunk heal summary. Emitted ONCE at
 * end-of-stream (just before `result`) on the streaming `parseFile` path.
 * Carries the applied `ReferenceFix[]` (entities in the preceding
 * `entity-chunk` messages already carry HEALED values). The client forwards
 * it to `onHealSummary` so the editorStore can build the bilingual transcript.
 */
type HealSummaryOutMsg = {
  id: string;
  type: "heal-summary";
  fixes: ReferenceFix[];
};
type ErrorOutMsg = { id: string; type: "error"; message: string };
type AbortedOutMsg = { id: string; type: "aborted" };
type WorkerOutMsg =
  | ProgressOutMsg
  | ResultOutMsg
  | EntityChunkOutMsg
  | HealSummaryOutMsg
  | ErrorOutMsg
  | AbortedOutMsg;

/**
 * Phase 4 Wave 2 Task 4 — chunk callback fired for each `entity-chunk`
 * message from the worker. Always batched — each invocation receives an
 * array of entities all belonging to the same `spaceId`. Multiple
 * invocations may target different spaces and may interleave; within a
 * single spaceId the entities arrive in append order.
 */
export type WorkerImportEntityChunkCallback = (
  entities: DrawingEntity[],
  spaceId: string,
) => void;

/**
 * PR1b Phase 2b — heal-summary callback fired ONCE per streaming import (just
 * before the result resolves) with the per-chunk heal fixes the worker applied.
 * Empty array ≡ a clean stream (no dangling refs); the caller stays silent.
 */
export type WorkerImportHealSummaryCallback = (fixes: ReferenceFix[]) => void;

// Section 0 — worker-realm measures arrive in the result message. Cache
// the latest snapshot on globalThis so the runner can pick them up after
// the import settles. Cleared at the start of each new worker call.
function publishWorkerMeasures(
  measures: Array<{ name: string; startTime: number; duration: number }> | undefined,
): void {
  if (typeof globalThis === "undefined") return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__section0_workerMeasures = measures ?? [];
}

/**
 * Minimal subset of the native `Worker` interface we need. Declaring it
 * explicitly (instead of relying on `typeof Worker`) makes test mocking
 * cleaner and keeps the factory dependency injection explicit.
 */
export interface WorkerLike {
  postMessage(msg: unknown): void;
  terminate(): void;
  addEventListener(
    type: "message",
    listener: (ev: MessageEvent<WorkerOutMsg>) => void,
  ): void;
  addEventListener(
    type: "error",
    listener: (ev: ErrorEvent) => void,
  ): void;
}

/**
 * Factory that creates a fresh worker instance. Extracted so tests
 * can supply a fake implementation without needing a real
 * `new URL(..., import.meta.url)` setup (which Vite rewrites at build
 * time, making it unusable in Vitest jsdom).
 */
export type WorkerFactory = () => WorkerLike;

function defaultWorkerFactory(): WorkerLike {
  return new Worker(
    new URL("../workers/dxf-parser.worker.ts", import.meta.url),
    { type: "module" },
  ) as unknown as WorkerLike;
}

/**
 * Start parsing a DXF source string in a dedicated Web Worker.
 *
 * Returns a handle with a `promise` that resolves with the parsed
 * document and an `abort()` function that requests cancellation.
 * Each call spins up a **fresh** worker and terminates it on completion
 * (success, error, or abort) — keeps the memory footprint flat.
 *
 * @param source      The raw DXF string (already decoded from CP1251/UTF-8)
 * @param onProgress  Callback invoked for every progress tick
 * @param factory     (test only) Override the worker factory
 */
export function startDxfImportInWorker(
  source: string,
  onProgress?: WorkerImportProgressCallback,
  factory: WorkerFactory = defaultWorkerFactory,
  // Phase 4 Wave 2 Task 4 — accept an `onEntityChunk` for API symmetry
  // with `startDxfImportFromFileInWorker`. The text path's worker
  // (`runParse`) currently does NOT emit chunk messages — small/medium
  // imports already work without streaming, so we did not add the risk
  // of a parallel code path. If a chunk message ever arrives here it
  // is forwarded to the callback all the same; otherwise the option is
  // a no-op.
  onEntityChunk?: WorkerImportEntityChunkCallback,
): WorkerImportResult {
  const worker = factory();
  const id = globalThis.crypto.randomUUID();

  let resolvePromise: (doc: DrawingDocument) => void = () => {};
  let rejectPromise: (err: Error) => void = () => {};
  const promise = new Promise<DrawingDocument>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  let settled = false;
  const finish = (terminate: boolean): void => {
    if (settled) return;
    settled = true;
    if (terminate) {
      worker.terminate();
    }
  };

  // Section 0 — track the gap between "worker started parse" and "main
  // thread received the result message". Includes worker work plus
  // structured-clone deserialization on the main thread.
  const tClientReceive = section0MarkStart("client:receive");

  worker.addEventListener("message", (ev: MessageEvent<WorkerOutMsg>) => {
    const msg = ev.data;
    if (!msg || msg.id !== id) return;
    switch (msg.type) {
      case "progress":
        recordWorkerProgressMsg();
        onProgress?.(
          msg.phase,
          msg.percent,
          msg.itemsProcessed,
          msg.itemsTotal,
        );
        return;
      case "entity-chunk":
        recordWorkerChunkMsg(msg.entities.length);
        onEntityChunk?.(msg.entities, msg.spaceId);
        return;
      case "result":
        section0MeasureSince("client:receive", tClientReceive);
        publishWorkerMeasures(msg.section0Measures);
        resolvePromise(msg.document);
        finish(true);
        return;
      case "error":
        rejectPromise(new Error(msg.message));
        finish(true);
        return;
      case "aborted":
        rejectPromise(new DOMException("Aborted", "AbortError"));
        finish(true);
        return;
    }
  });

  worker.addEventListener("error", (ev: ErrorEvent) => {
    // A worker-level error (e.g. module load failure) surfaces here
    // before our `message` protocol ever kicks in. Translate into a
    // rejection so the caller can fall back to the sync path.
    const message =
      (ev && typeof ev.message === "string" && ev.message.length > 0
        ? ev.message
        : "Worker error");
    rejectPromise(new Error(message));
    finish(true);
  });

  const parseMsg: ParseInMsg = { id, cmd: "parse", source };
  worker.postMessage(parseMsg);

  return {
    promise,
    abort: () => {
      if (settled) return;
      const abortMsg: AbortInMsg = { id, cmd: "abort" };
      try {
        worker.postMessage(abortMsg);
      } catch {
        // Worker may already be terminated — ignore.
      }
    },
  };
}

/**
 * `File`-handle variant of {@link startDxfImportInWorker}. The `File`
 * object is transferred to the worker by reference (structured clone of
 * a `File` does not copy the underlying bytes — it shares the blob
 * handle), and the worker decodes via codepage-aware `decodeDxfBytes`
 * (`$DWGCODEPAGE` → TextDecoder, applied to `await file.arrayBuffer()`)
 * inside its own thread. This keeps the giant source string off the main
 * thread
 * entirely — critical for files in the 100–500 MB range where the
 * decoded UTF-16 string approaches half a gigabyte and structured-clone
 * across threads would OOM the main thread.
 *
 * Same return shape as {@link startDxfImportInWorker} — caller awaits
 * `promise` for the `DrawingDocument` and may call `abort()` at any time.
 */
export function startDxfImportFromFileInWorker(
  file: File,
  onProgress?: WorkerImportProgressCallback,
  factory: WorkerFactory = defaultWorkerFactory,
  // Phase 4 Wave 2 Task 4 — chunk callback. The worker streams entities
  // through this hook AS THEY ARE CONVERTED — each invocation receives
  // a batch of ~5000 entities all belonging to the same `spaceId`.
  // The result message that resolves `promise` then carries empty
  // `space.entities[]` arrays. Callers MUST drain the chunks into
  // wherever they want the entities (typically the editorStore compact
  // cache via `appendToCompactCache`) before treating the resolved
  // document as authoritative — `populateCompactCacheFromDocument(doc)`
  // on the resolved doc would silently no-op against the empty arrays.
  onEntityChunk?: WorkerImportEntityChunkCallback,
  // PR1b Phase 2b — heal-summary callback. Fired once at end-of-stream with the
  // per-chunk heal fixes the worker applied (entities in the chunk messages
  // already carry healed values). Empty list ≡ clean stream.
  onHealSummary?: WorkerImportHealSummaryCallback,
): WorkerImportResult {
  const worker = factory();
  const id = globalThis.crypto.randomUUID();

  let resolvePromise: (doc: DrawingDocument) => void = () => {};
  let rejectPromise: (err: Error) => void = () => {};
  const promise = new Promise<DrawingDocument>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  let settled = false;
  const finish = (terminate: boolean): void => {
    if (settled) return;
    settled = true;
    if (terminate) {
      worker.terminate();
    }
  };

  // Section 0 — see comment in `startDxfImportInWorker`. Same span name
  // so the runner can read it without branching on path variant.
  const tClientReceive = section0MarkStart("client:receive");

  worker.addEventListener("message", (ev: MessageEvent<WorkerOutMsg>) => {
    const msg = ev.data;
    if (!msg || msg.id !== id) return;
    switch (msg.type) {
      case "progress":
        recordWorkerProgressMsg();
        onProgress?.(
          msg.phase,
          msg.percent,
          msg.itemsProcessed,
          msg.itemsTotal,
        );
        return;
      case "entity-chunk":
        recordWorkerChunkMsg(msg.entities.length);
        onEntityChunk?.(msg.entities, msg.spaceId);
        return;
      case "heal-summary":
        onHealSummary?.(msg.fixes);
        return;
      case "result":
        section0MeasureSince("client:receive", tClientReceive);
        publishWorkerMeasures(msg.section0Measures);
        resolvePromise(msg.document);
        finish(true);
        return;
      case "error":
        rejectPromise(new Error(msg.message));
        finish(true);
        return;
      case "aborted":
        rejectPromise(new DOMException("Aborted", "AbortError"));
        finish(true);
        return;
    }
  });

  worker.addEventListener("error", (ev: ErrorEvent) => {
    const message =
      (ev && typeof ev.message === "string" && ev.message.length > 0
        ? ev.message
        : "Worker error");
    rejectPromise(new Error(message));
    finish(true);
  });

  const parseMsg: ParseFileInMsg = {
    id,
    cmd: "parseFile",
    file,
    // Phase 4 Wave 2 (П35Р follow-up) — opt the worker into the
    // streaming codepath only when the caller actually wired up an
    // `onEntityChunk` handler. With no consumer, streaming would
    // produce a result document with empty `space.entities[]` arrays
    // AND fire chunk messages into `/dev/null`, dropping every entity
    // silently. The non-streaming default keeps the legacy
    // `space.entities`-populated path that all existing call sites
    // depend on.
    stream: onEntityChunk !== undefined,
  };
  worker.postMessage(parseMsg);

  return {
    promise,
    abort: () => {
      if (settled) return;
      const abortMsg: AbortInMsg = { id, cmd: "abort" };
      try {
        worker.postMessage(abortMsg);
      } catch {
        // Worker may already be terminated — ignore.
      }
    },
  };
}
